/**
 * groups.ts — membership helpers for node groups.
 *
 * A node belongs to a group when EITHER:
 *  (a) its CENTRE point falls inside the group's rectangle (spatial), OR
 *  (b) the group's id is listed in `node.groupIds` (out-of-band assignment).
 *
 * The two sources are additive (union, deduped). Spatial is the default and
 * makes dragging a node in/out of a group automatic; `groupIds` extends it so
 * a node can belong to a group whose rectangle it doesn't intersect. The sim
 * engine stays unaware of groups entirely — only this module + the modal that
 * edits `groupIds` know about explicit membership.
 *
 * Pure functions, no React.
 */

import type { GraphNode, NodeGroup } from '../engine/types';

/** Assumed node box, matching the CSS width (140px, see `.actor-node` in
 *  style.css) and the routing module's height assumption (60px). The exact
 *  pixel doesn't matter for membership — only the centre-vs-rect test. */
export const NODE_W = 140;
export const NODE_H = 60;

/** Centre of a node in flow coordinates, using its authored position (or the
 *  origin when absent). */
export function nodeCenter(node: GraphNode): { x: number; y: number } {
  const p = node.position ?? { x: 0, y: 0 };
  return { x: p.x + NODE_W / 2, y: p.y + NODE_H / 2 };
}

/** Is a point inside the group's rectangle? */
export function rectContains(
  rect: { x: number; y: number; w: number; h: number },
  p: { x: number; y: number },
): boolean {
  return p.x >= rect.x && p.x <= rect.x + rect.w && p.y >= rect.y && p.y <= rect.y + rect.h;
}

/** Stable colour for a group when none is authored. The canonical palette +
 *  hash live in `./palette` (GROUP_PALETTE / groupColor) so the rect, the
 *  sidebar dot, and the NodeModal pill all agree. */
export { groupColor } from './palette';

import { groupColor } from './palette';

/** Resolve a group's effective colour (authored override wins over hash). */
export function groupColorOf(group: NodeGroup): string {
  return group.color ?? groupColor(group.id);
}

/** A node belongs to a group when its centre is inside the rect OR it carries
 *  the group's id in `node.groupIds` (out-of-band assignment). The two sources
 *  are additive — spatial membership is the default; `groupIds` extends it so a
 *  node can belong to a group whose rectangle it doesn't intersect. */
export function nodeInGroup(node: GraphNode, group: NodeGroup): boolean {
  if (rectContains(group.rect, nodeCenter(node))) return true;
  return !!node.groupIds && node.groupIds.includes(group.id);
}

/** Nodes that belong to the group (spatial OR explicit). Order follows the
 *  input node list so the sidebar / modal are stable across ticks. */
export function membersOf(group: NodeGroup, nodes: GraphNode[]): GraphNode[] {
  return nodes.filter((n) => nodeInGroup(n, group));
}

/** Member node ids as a Set — the shape buildGroupBalanceSheet consumes. */
export function memberIdsOf(group: NodeGroup, nodes: GraphNode[]): Set<string> {
  const out = new Set<string>();
  for (const n of nodes) {
    if (nodeInGroup(n, group)) out.add(n.id);
  }
  return out;
}

/** Every group a node currently belongs to (a node may be in several
 *  overlapping groups, plus any it's explicitly assigned to via `groupIds`).
 *  Used by the sidebar legend / membership dots. */
export function groupsOfNode(
  nodeId: string,
  groups: NodeGroup[],
  nodes: GraphNode[],
): NodeGroup[] {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return [];
  return groups.filter((g) => nodeInGroup(node, g));
}
